import React from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
  useTheme,
  Button,
} from "@mui/material";
import {
  Image as ImageIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import { useHistory } from "react-router-dom";
import SourceImageThumbnail from "./SourceImageThumbnail";
import {
  getImageContentTypeLabel,
  formatPageNumber,
} from "../../utils/imageSourceUtils";
import type { SearchResult } from "../../types/citations";

type ImageSourceCardProps = {
  source: SearchResult;
  index: number;
  /** DOM id for citation scroll-targeting; defaults to ``source-${index}``. */
  domId?: string;
};

/**
 * Card component for displaying image sources in the expanded sources view.
 * Shows the image thumbnail, VLM description, and document context.
 */
const ImageSourceCard: React.FC<ImageSourceCardProps> = ({
  source,
  index,
  domId,
}) => {
  const theme = useTheme();
  const history = useHistory();

  const imageArtifactId = source?.data_chunk?.chunk_metadata?.image_artifact_id;
  const pageNumber = source?.data_chunk?.chunk_metadata?.page_number;
  const contentType = source?.data_chunk?.chunk_data?.content_type;
  const description = source?.data_chunk?.chunk_data?.data;
  const fileName = source?.source_data_record?.data?.file_name;
  const datasetName = source?.dataset?.name;
  const sdrId = source?.source_data_record?.id;
  const datasetId = source?.dataset?.id;

  const handleViewPage = () => {
    if (datasetId && sdrId && pageNumber) {
      const page = Array.isArray(pageNumber) ? pageNumber[0] : pageNumber;
      history.push(
        `/datasets/${datasetId}/document/${sdrId}?page_number=${page}`,
      );
    }
  };

  if (!imageArtifactId) {
    return null;
  }

  return (
    <Card
      id={domId ?? `source-${index}`}
      data-testid={`aa-source-image-card-${index}`}
      sx={{
        mb: 2,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <CardContent>
        {/* Header */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <Chip
            label={index}
            size="small"
            variant="outlined"
            sx={{
              minWidth: "28px",
              height: "24px",
              fontSize: "0.75rem",
              fontWeight: 600,
            }}
          />
          <Chip
            icon={<ImageIcon sx={{ fontSize: "1rem" }} />}
            label={getImageContentTypeLabel(contentType)}
            size="small"
            color="secondary"
            variant="outlined"
          />
          {datasetName && (
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", fontWeight: 500 }}
            >
              {datasetName}
            </Typography>
          )}
        </Stack>

        {/* Image and context */}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          {/* Image thumbnail */}
          <Box sx={{ flexShrink: 0 }}>
            <SourceImageThumbnail
              imageArtifactId={imageArtifactId}
              fileName={fileName}
              pageNumber={pageNumber}
              size="medium"
            />
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1 }}>
            {/* Document context */}
            {(fileName || pageNumber) && (
              <Box sx={{ mb: 1.5 }}>
                {fileName && (
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ color: "text.primary", mb: 0.25 }}
                  >
                    {fileName}
                  </Typography>
                )}
                {pageNumber && (
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    Page {formatPageNumber(pageNumber)}
                  </Typography>
                )}
              </Box>
            )}

            {/* VLM description */}
            {description && (
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  lineHeight: 1.5,
                  display: "-webkit-box",
                  WebkitLineClamp: 4,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {description}
              </Typography>
            )}
          </Box>
        </Stack>

        {/* Footer with view button */}
        {datasetId && sdrId && pageNumber && (
          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
            <Button
              data-testid={`aa-source-image-view-btn-${index}`}
              size="small"
              variant="outlined"
              startIcon={<OpenInNewIcon />}
              onClick={handleViewPage}
            >
              View Page
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ImageSourceCard;
